import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './styles/tokens.css';
import './styles/darkTheme.css';
import { initTheme } from './utils/theme';
import DialogHost from './components/DialogHost';
import reportWebVitals from './reportWebVitals';
import {
  createBrowserRouter,
  RouterProvider,
  useNavigate
} from "react-router-dom";
import { restoreAuthFromStorage } from './globalfunctions';
import './utils/authFetch';
import Home from './pages/Home';
import About from './pages/About';
import Blog from './pages/Blog';
import LoginForm from './pages/LoginForm';
import RegisterForm from './pages/RegisterForm';
import Invoices from './pages/Invoices';
import EditInvoice from './pages/EditInvoice';
import Sales from './pages/Sales';
import Users from './pages/Users';
import AddInvoice from './pages/AddInvoice';
import CreateCustomer from './pages/CreateCustomer';
import Customer from './pages/Customer';
import Items from './pages/Items';
import Settings from './components/Settings';
import Transactions from './pages/Transactions';
import Tickets from './pages/Tickets';
import TicketDetail from './pages/TicketDetail';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import LeadStats from './pages/LeadStats';
import MailSettings from './pages/MailSettings';
import MailBlocklist from './pages/MailBlocklist';
import TicketRules from './pages/TicketRules';
import TicketHours from './pages/TicketHours';
import TicketStats from './pages/TicketStats';
import AuditLogs from './pages/AuditLogs';
import Subscriptions from './pages/Subscriptions';
import Quotes from './pages/Quotes';
import Uptime from './pages/Uptime';
import CPanel from './pages/CPanel';
import Hosting from './pages/Hosting';
import PortalHome from './pages/PortalHome';
import PortalInvoices from './pages/PortalInvoices';
import PortalTickets from './pages/PortalTickets';
import PortalTicketDetail from './pages/PortalTicketDetail';
import PortalDomains from './pages/PortalDomains';
import PortalSubscriptions from './pages/PortalSubscriptions';
import PortalUptime from './pages/PortalUptime';
import PortalCpanel from './pages/PortalCpanel';
import PortalHelp from './pages/PortalHelp';
import PortalQuotes from './pages/PortalQuotes';
import PortalAccount from './pages/PortalAccount';
import KnowledgeBase from './pages/KnowledgeBase';
import PasswordReset from './pages/PasswordReset';
import ForgotPasswordForm from './pages/ForgotPasswordForm';
import MFAGuard from './components/MFAGuard';
import ProtectedRoute from './components/ProtectedRoute';
import { PERMISSIONS, ROLES } from './utils/roleManager';

const router = createBrowserRouter([
  {
    path: "",
    element: <LoginForm />,
  },
  {
    path: "/Login",
    element: <LoginForm />,
  },
  {
    path: "/ForgotPassword",
    element: <ForgotPasswordForm />,
  },
  {
    path: "/ResetPassword",
    element: <PasswordReset />,
  },
  {
    path: "/Register",
    element: <RegisterForm />,
  },
  {
    path: "/Settings",
    element: (
      <MFAGuard>
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/Home",
    element: (
      <MFAGuard>
        <ProtectedRoute>
          <Home />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/About",
    element: <About />,
  },
  {
    path: "/Blog",
    element: <Blog />,
  }, 
  {
    path: "/invoices",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_INVOICES]}>
          <Invoices />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/invoices/editinvoice/:invoiceNumber",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.EDIT_INVOICES]}>
          <EditInvoice />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/items",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_ITEMS]}>
          <Items />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/sales",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_SALES_DATA]}>
          <Sales />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/addinvoice",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.CREATE_INVOICES]}>
          <AddInvoice />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/transactions",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_FINANCIAL_REPORTS]}>
          <Transactions />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/users",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_USERS]}>
          <Users />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/createcustomer",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.CREATE_CUSTOMERS]}>
          <CreateCustomer />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/customer/:userId",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_CUSTOMERS]}>
          <Customer />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/tickets",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_TICKETS]}>
          <Tickets />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/tickets/:ticketId",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_TICKETS]}>
          <TicketDetail />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/leads",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_LEADS]}>
          <Leads />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/leads/:leadId",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_LEADS]}>
          <LeadDetail />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/lead-stats",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_LEADS]}>
          <LeadStats />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/audit-logs",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_AUDIT_LOGS]}>
          <AuditLogs />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/subscriptions",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_SUBSCRIPTIONS]}>
          <Subscriptions />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/quotes",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_QUOTES]}>
          <Quotes />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/uptime",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_UPTIME]}>
          <Uptime />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/cpanel",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_CPANEL]}>
          <CPanel />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/hosting",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.MANAGE_HOSTING]}>
          <Hosting />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/portal",
    element: (
      <MFAGuard>
        <ProtectedRoute allowedRoles={[ROLES.CUSTOMER]}>
          <PortalHome />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/portal/quotes",
    element: (
      <MFAGuard>
        <ProtectedRoute allowedRoles={[ROLES.CUSTOMER]}>
          <PortalQuotes />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/portal/account",
    element: (
      <MFAGuard>
        <ProtectedRoute allowedRoles={[ROLES.CUSTOMER]}>
          <PortalAccount />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/portal/invoices",
    element: (
      <MFAGuard>
        <ProtectedRoute allowedRoles={[ROLES.CUSTOMER]}>
          <PortalInvoices />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/portal/tickets",
    element: (
      <MFAGuard>
        <ProtectedRoute allowedRoles={[ROLES.CUSTOMER]}>
          <PortalTickets />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/portal/tickets/:id",
    element: (
      <MFAGuard>
        <ProtectedRoute allowedRoles={[ROLES.CUSTOMER]}>
          <PortalTicketDetail />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/portal/domains",
    element: (
      <MFAGuard>
        <ProtectedRoute allowedRoles={[ROLES.CUSTOMER]}>
          <PortalDomains />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/portal/subscriptions",
    element: (
      <MFAGuard>
        <ProtectedRoute allowedRoles={[ROLES.CUSTOMER]}>
          <PortalSubscriptions />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/portal/uptime",
    element: (
      <MFAGuard>
        <ProtectedRoute allowedRoles={[ROLES.CUSTOMER]}>
          <PortalUptime />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/portal/cpanel",
    element: (
      <MFAGuard>
        <ProtectedRoute allowedRoles={[ROLES.CUSTOMER]}>
          <PortalCpanel />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/portal/help",
    element: (
      <MFAGuard>
        <ProtectedRoute allowedRoles={[ROLES.CUSTOMER]}>
          <PortalHelp />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/portal/help/:id",
    element: (
      <MFAGuard>
        <ProtectedRoute allowedRoles={[ROLES.CUSTOMER]}>
          <PortalHelp />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/knowledge-base",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_TICKETS]}>
          <KnowledgeBase />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/mail-settings",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.MANAGE_MAIL_SETTINGS]}>
          <MailSettings />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/mail-blocklist",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.MANAGE_MAIL_SETTINGS]}>
          <MailBlocklist />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/ticket-rules",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.MANAGE_MAIL_SETTINGS]}>
          <TicketRules />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/ticket-hours",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.MANAGE_MAIL_SETTINGS]}>
          <TicketHours />
        </ProtectedRoute>
      </MFAGuard>
    ),
  },
  {
    path: "/ticket-stats",
    element: (
      <MFAGuard>
        <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_TICKETS]}>
          <TicketStats />
        </ProtectedRoute>
      </MFAGuard>
    ),
  }
]);

const AppWithAuth = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Session expiry is now detected for real by authFetch (see
        // src/utils/authFetch.js, imported once below) off actual 401/403
        // responses, which silently refreshes instead of forcing a logout.
        // simpleSessionTimer's old startMonitoring() hard-logged-out on a
        // fixed client-side 1-hour clock regardless of whether the session
        // could actually be refreshed - keeping it running would silently
        // undo the whole point of silent refresh, so it's no longer
        // started here (startSession/clearSession bookkeeping elsewhere is
        // untouched, harmless).
        await restoreAuthFromStorage();
      } catch (error) {
        console.error('Failed to restore authentication:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: '48px', 
            marginBottom: '20px' 
          }}>🔄</div>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <RouterProvider router={router} />
      <DialogHost />
    </>
  );
};

initTheme();
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <AppWithAuth />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
